import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  MapPinIcon,
  CurrencyYenIcon,
  ClockIcon,
  CalendarIcon,
  UserIcon,
  StarIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  HeartIcon,
  PlayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { API_BASE } from '../lib/config';

// API base already points to "/api" per shared config
const API = API_BASE;

const JobDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [preferredWorkStartTime, setPreferredWorkStartTime] = useState('');
  const [preferredWorkEndTime, setPreferredWorkEndTime] = useState('');
  const [hasApplied, setHasApplied] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [identityStatus, setIdentityStatus] = useState(null);
  const [eligibilityIssues, setEligibilityIssues] = useState([]);
  const [isEligible, setIsEligible] = useState(true);

  useEffect(() => {
    fetchJob();
    if (user) {
      checkApplicationStatus();
      checkFavoriteStatus();
      checkIdentityStatus();
      checkApplicationEligibility();
    }
  }, [id, user]);

  const checkApplicationEligibility = async () => {
    try {
      const response = await fetch(`${API}/users/application-eligibility`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIsEligible(data.eligible);
        setEligibilityIssues(data.issues || []);
      }
    } catch (error) {
      console.error('Error checking application eligibility:', error);
    }
  };

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${id}`);
      setJob(response.data);
    } catch (error) {
      toast.error('求人の取得に失敗しました');
      navigate('/jobs');
    } finally {
      setLoading(false);
    }
  };

  const checkApplicationStatus = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(`${API}/applications`, { headers });
      const hasAppliedToJob = response.data.some(app => app.job_id === id);
      setHasApplied(hasAppliedToJob);
    } catch (error) {
      console.error('Failed to check application status:', error);
    }
  };

  const checkFavoriteStatus = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(`${API}/favorites`, { headers });
      const favorites = response.data;
      const favorited = favorites.some(fav => fav.job_id === id);
      setIsFavorite(favorited);
    } catch (error) {
      console.error('Failed to check favorite status:', error);
    }
  };

  const checkIdentityStatus = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      };
      const response = await axios.get(`${API}/identity-verification/status`, { headers });
      setIdentityStatus(response.data);
    } catch (error) {
      console.error('Failed to check identity status:', error);
    }
  };

  const handleApply = async () => {
    if (!user) {
      toast.error('ログインしてください');
      navigate('/login');
      return;
    }

    if (user.user_type !== 'job_seeker') {
      toast.error('ワーカーのみ参加申込できます');
      return;
    }

    // 応募資格チェック
    try {
      const eligibilityResponse = await fetch(`${API}/users/application-eligibility`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (eligibilityResponse.ok) {
        const eligibilityData = await eligibilityResponse.json();

        if (!eligibilityData.eligible) {
          // 不備がある場合は詳細メッセージを表示
          const issues = eligibilityData.issues.join(' ');
          toast.error(issues);
          return;
        }
      } else {
        toast.error('応募資格の確認に失敗しました');
        return;
      }
    } catch (error) {
      console.error('Error checking eligibility:', error);
      toast.error('応募資格の確認に失敗しました');
      return;
    }

    // 希望勤務時間のバリデーション
    if (!preferredWorkStartTime || !preferredWorkEndTime) {
      toast.error('希望勤務時間を入力してください');
      return;
    }

    // 時間の形式チェック（HH:MM）
    const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
    if (!timePattern.test(preferredWorkStartTime) || !timePattern.test(preferredWorkEndTime)) {
      toast.error('時間は HH:MM の形式で入力してください');
      return;
    }

    // 開始時間と終了時間の順序チェック
    const startTime = new Date(`2000-01-01T${preferredWorkStartTime}:00`);
    const endTime = new Date(`2000-01-01T${preferredWorkEndTime}:00`);
    if (startTime >= endTime) {
      toast.error('終了時間は開始時間より後にしてください');
      return;
    }

    setApplying(true);
    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      };

      // Use NEW Instant Match API
      const response = await axios.post(`${API}/jobs/${id}/apply`, {
        message: applicationMessage,
        preferred_work_start_time: preferredWorkStartTime,
        preferred_work_end_time: preferredWorkEndTime
      }, { headers });

      toast.success(response.data.message || 'マッチングが確定しました！');
      setHasApplied(true);

      // Emit admin event
      try { axios.post(`${API_BASE}/admin/events/application-submitted`).catch(() => { }); } catch (_) { }

      // Navigate to Chat Room if ID returned
      if (response.data.chat_room_id) {
        // Add small delay for toast to be seen
        setTimeout(() => {
          navigate(`/messages/${response.data.chat_room_id}`);
        }, 1500);
      }

    } catch (error) {
      toast.error(error.response?.data?.detail || '応募に失敗しました');
    } finally {
      setApplying(false);
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      toast.error('ログインが必要です');
      navigate('/login');
      return;
    }

    if (user.user_type !== 'job_seeker') {
      toast.error('求職者のみお気に入り登録できます');
      return;
    }

    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.post(`${API}/favorites/${id}`, null, { headers });
      setIsFavorite(response.data.is_favorite);
      toast.success(response.data.message);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'お気に入りの操作に失敗しました';
      toast.error(errorMessage);
    }
  };

  const nextImage = () => {
    if (job && job.images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % job.images.length);
    }
  };

  const prevImage = () => {
    if (job && job.images.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + job.images.length) % job.images.length);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'status-badge status-active',
      filled: 'status-badge status-rejected',
      cancelled: 'status-badge status-rejected'
    };

    const labels = {
      active: '募集中',
      filled: '募集終了',
      cancelled: 'キャンセル'
    };

    return (
      <span className={badges[status] || badges.active}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-8 animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-6"></div>
            <div className="h-32 bg-gray-200 rounded mb-6"></div>
            <div className="h-12 bg-gray-200 rounded w-full"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">求人が見つかりません</h1>
          <button
            onClick={() => navigate('/jobs')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            求人一覧に戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Image Section */}
      {job.images && job.images.length > 0 && (
        <div className="relative h-64 md:h-80 bg-gray-900">
          <img
            src={job.thumbnail_image || job.images[currentImageIndex]}
            alt={job.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>

          {/* Image Navigation */}
          {job.images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 p-2 rounded-full transition-all"
              >
                <ChevronLeftIcon className="h-6 w-6 text-gray-700" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 p-2 rounded-full transition-all"
              >
                <ChevronRightIcon className="h-6 w-6 text-gray-700" />
              </button>
            </>
          )}

          {/* Image Indicators */}
          {job.images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
              {job.images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all ${index === currentImageIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                    }`}
                />
              ))}
            </div>
          )}

          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="absolute top-4 left-4 bg-white bg-opacity-80 hover:bg-opacity-100 p-2 rounded-full transition-all"
          >
            <ChevronLeftIcon className="h-6 w-6 text-gray-700" />
          </button>

          {/* Favorite Button */}
          {user && user.user_type === 'job_seeker' && (
            <button
              onClick={toggleFavorite}
              className="absolute top-4 right-4 bg-white bg-opacity-80 hover:bg-opacity-100 p-2 rounded-full transition-all"
            >
              {isFavorite ? (
                <HeartIconSolid className="h-6 w-6 text-red-500" />
              ) : (
                <HeartIcon className="h-6 w-6 text-gray-700" />
              )}
            </button>
          )}
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Job Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{job.title}</h1>
              <div className="flex items-center text-gray-600 mb-2">
                <UserIcon className="w-5 h-5 mr-2" />
                <span className="font-medium">{job.employer_name}</span>
                <StarIcon className="w-4 h-4 ml-2 text-yellow-400" />
                <span className="text-sm">4.5</span>
              </div>
              <div className="flex items-center text-gray-500 text-sm">
                <MapPinIcon className="w-4 h-4 mr-1" />
                <span>{job.prefecture} {job.city}</span>
              </div>
            </div>

            <div className="text-right">
              <div className="text-3xl font-bold text-green-600 mb-1">
                {job.hourly_rate.toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">時給</div>
            </div>
          </div>

          {/* Key Info Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-3 rounded-lg text-center">
              <ClockIcon className="w-6 h-6 text-blue-600 mx-auto mb-1" />
              <div className="text-sm font-medium text-blue-900">{job.duration_hours}時間</div>
              <div className="text-xs text-blue-600">勤務時間</div>
            </div>

            <div className="bg-green-50 p-3 rounded-lg text-center">
              <UserIcon className="w-6 h-6 text-green-600 mx-auto mb-1" />
              <div className="text-sm font-medium text-green-900">{job.required_people}名</div>
              <div className="text-xs text-green-600">募集人数</div>
            </div>

            <div className="bg-purple-50 p-3 rounded-lg text-center">
              <CalendarIcon className="w-6 h-6 text-purple-600 mx-auto mb-1" />
              <div className="text-sm font-medium text-purple-900">
                {formatDate(job.start_date).split(' ')[0]}
              </div>
              <div className="text-xs text-purple-600">開始日</div>
            </div>

            <div className="bg-orange-50 p-3 rounded-lg text-center">
              <BriefcaseIcon className="w-6 h-6 text-orange-600 mx-auto mb-1" />
              <div className="text-sm font-medium text-orange-900">
                {job.current_applicants}/{job.max_applicants}
              </div>
              <div className="text-xs text-orange-600">応募状況</div>
            </div>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2">
            {job.same_day_ok && (
              <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                即日OK
              </span>
            )}
            {!job.experience_required && (
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                未経験OK
              </span>
            )}
            <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded-full">
              {job.category}
            </span>
          </div>
        </div>

        {/* Media Section */}
        {(job.images && job.images.length > 0) || (job.videos && job.videos.length > 0) && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">職場の様子</h2>

            {/* Images Grid */}
            {job.images && job.images.length > 0 && (
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">写真</h3>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                  {job.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentImageIndex(index);
                        setShowImageModal(true);
                      }}
                      className="aspect-square bg-gray-200 rounded-lg overflow-hidden hover:opacity-80 transition-opacity"
                    >
                      <img
                        src={image}
                        alt={`職場の様子 ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Videos */}
            {job.videos && job.videos.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">動画</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {job.videos.map((video, index) => (
                    <button
                      key={index}
                      onClick={() => setShowVideoModal(true)}
                      className="relative aspect-video bg-gray-200 rounded-lg overflow-hidden hover:opacity-80 transition-opacity"
                    >
                      <video
                        src={video}
                        className="w-full h-full object-cover"
                        muted
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                        <PlayIcon className="h-12 w-12 text-white" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Job Details */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">詳細情報</h2>

          <div className="space-y-4">
            <div>
              <h3 className="font-medium text-gray-900 mb-2">仕事内容</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{job.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">勤務地</h3>
                <p className="text-gray-700 font-bold">{job.location}</p>
                <p className="text-sm text-gray-600 mb-2">
                  {job.prefecture} {job.city} {job.address} {job.building}
                </p>

                {/* Access Info */}
                {job.access_info && (
                  <div className="text-xs text-gray-500 mb-2 bg-gray-50 p-2 rounded">
                    <strong>アクセス:</strong> {job.access_info}
                  </div>
                )}

                {/* Map Embed */}
                <div className="w-full h-48 bg-gray-200 rounded-lg overflow-hidden mb-2">
                  <iframe
                    title="Workplace Map"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    style={{ border: 0 }}
                    src={`https://maps.google.com/maps?q=${encodeURIComponent((job.prefecture || '') + (job.city || '') + (job.address || ''))}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                    allowFullScreen
                  ></iframe>
                </div>

                {/* Navigate Button */}
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent((job.prefecture || '') + (job.city || '') + (job.address || ''))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-full px-4 py-2 bg-white border border-blue-500 text-blue-600 rounded-md hover:bg-blue-50 text-sm font-medium"
                >
                  <MapPinIcon className="w-4 h-4 mr-1" />
                  現在地からのルートを表示
                </a>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-2">勤務時間</h3>
                <p className="text-gray-700">{job.work_hours || `${job.duration_hours}時間`}</p>
                <p className="text-sm text-gray-500">
                  {formatDate(job.start_date)} - {formatDate(job.end_date)}
                </p>
                {/* Dress Code (in Time col or separate? Let's put here if relevant, or below) */}
              </div>
            </div>
          </div>

          {job.requirements && job.requirements.length > 0 && (
            <div>
              <h3 className="font-medium text-gray-900 mb-2">応募条件</h3>
              <ul className="text-gray-700 space-y-1">
                {job.requirements.map((req, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-600 mr-2"></span>
                    {req}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {job.items_to_bring && job.items_to_bring.length > 0 && (
            <div>
              <h3 className="font-medium text-gray-900 mb-2">持ち物</h3>
              <div className="flex flex-wrap gap-2">
                {job.items_to_bring.map((item, index) => (
                  <span
                    key={index}
                    className="bg-yellow-100 text-yellow-800 text-sm px-3 py-1 rounded-full"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {job.dress_code && (
            <div>
              <h3 className="font-medium text-gray-900 mb-2">服装・髪型</h3>
              <p className="text-gray-700">{job.dress_code}</p>
            </div>
          )}
        </div>
      </div>

      {/* Application Section */}
      {user && user.user_type === 'job_seeker' && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">応募</h2>

          {hasApplied ? (
            <div className="text-center py-8">
              <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">応募済み</h3>
              <p className="text-gray-600">この求人に応募済みです</p>
            </div>
          ) : job.current_applicants >= job.max_applicants ? (
            <div className="text-center py-8">
              <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XMarkIcon className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">募集終了</h3>
              <p className="text-gray-600">この求人の募集は終了しました</p>
            </div>
          ) : (
            <div>
              {/* 本人確認が未完了の場合の警告 */}
              {identityStatus && identityStatus.status !== 'completed' && (
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-medium text-yellow-800">本人確認が必要です</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        {identityStatus.status === 'not_started' && '求人に応募するには本人確認書類の提出が必要です。'}
                        {identityStatus.status === 'in_progress' && '本人確認の審査中です。審査完了後に応募できます。'}
                        {identityStatus.status === 'rejected' && '本人確認書類が差し戻されています。再提出してください。'}
                      </p>
                      {identityStatus.status !== 'in_progress' && (
                        <button
                          onClick={() => navigate('/identity-verification')}
                          className="mt-2 text-sm text-yellow-800 hover:text-yellow-900 underline"
                        >
                          本人確認に進む
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  希望勤務時間 <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">開始時間</label>
                    <input
                      type="time"
                      value={preferredWorkStartTime}
                      onChange={(e) => setPreferredWorkStartTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={identityStatus && identityStatus.status !== 'completed'}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">終了時間</label>
                    <input
                      type="time"
                      value={preferredWorkEndTime}
                      onChange={(e) => setPreferredWorkEndTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={identityStatus && identityStatus.status !== 'completed'}
                      required
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  働ける時間帯を選択してください（例：09:00 - 17:00）
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  応募メッセージ（任意）
                </label>
                <textarea
                  value={applicationMessage}
                  onChange={(e) => setApplicationMessage(e.target.value)}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="志望動機や自己PRなどをお書きください..."
                  disabled={identityStatus && identityStatus.status !== 'completed'}
                />
              </div>

              <button
                onClick={handleApply}
                disabled={applying || (identityStatus && identityStatus.status !== 'completed')}
                className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {applying ? '応募中...' :
                  (identityStatus && identityStatus.status !== 'completed') ? '本人確認が必要です' :
                    'この求人に応募する'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && job.images && (
        <ImageModal
          images={job.images}
          currentIndex={currentImageIndex}
          onClose={() => setShowImageModal(false)}
          onNext={nextImage}
          onPrev={prevImage}
        />
      )}

      {/* Video Modal */}
      {showVideoModal && job.videos && (
        <VideoModal
          videos={job.videos}
          onClose={() => setShowVideoModal(false)}
        />
      )}
    </div>

  );
};

// Image Modal Component
const ImageModal = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
      <div className="relative max-w-4xl max-h-full p-4">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
        >
          <XMarkIcon className="h-8 w-8" />
        </button>

        <img
          src={images[currentIndex]}
          alt={`職場の様子 ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain"
        />

        {images.length > 1 && (
          <>
            <button
              onClick={onPrev}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
            >
              <ChevronLeftIcon className="h-8 w-8" />
            </button>
            <button
              onClick={onNext}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
            >
              <ChevronRightIcon className="h-8 w-8" />
            </button>
          </>
        )}

        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm">
          {currentIndex + 1} / {images.length}
        </div>
      </div>
    </div>
  );
};

// Video Modal Component
const VideoModal = ({ videos, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
      <div className="relative max-w-4xl max-h-full p-4">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
        >
          <XMarkIcon className="h-8 w-8" />
        </button>

        <video
          src={videos[0]}
          controls
          autoPlay
          className="max-w-full max-h-full"
        />
      </div>
    </div>
  );
};

export default JobDetailPage;